/**
 * Hex SHA-256 of a request body, which every POST of ours must carry.
 *
 * ## Why this exists at all
 *
 * The site's two POST endpoints — `/prepare` (OG-SPEC §13.2) and `/report`
 * (REPORT-SPEC §7.2) — are Lambda function URLs reached through CloudFront's
 * origin access control, and **Lambda does not accept an unsigned payload**.
 * CloudFront signs what it forwards, and for a request with a body the payload
 * hash has to come from the caller, in `x-amz-content-sha256`.
 *
 * Without it the edge answers 403 before the function is invoked — no log line,
 * no metric, nothing at the origin that knows a request happened.
 *
 * The header cannot be worked around at the CDN either: allowlisting it for
 * forwarding is rejected outright (`The parameter Headers contains
 * x-amz-content-sha256 that is not allowed`) because CloudFront reads it to
 * build the signature rather than passing it through. That was found on a
 * production deploy. See the `/prepare` behaviour in `infra/lib/site-stack.ts`.
 *
 * ## Why it is shared rather than copied
 *
 * REPORT-SPEC §7.2 asks for it in as many words: "Share a small body-hash
 * helper rather than copy a subtly different implementation." A second copy
 * that hashed, say, the string rather than its UTF-8 bytes would produce a
 * signature mismatch — which surfaces as a 403 at the edge, on one endpoint
 * only, with the other endpoint working perfectly and no error naming the
 * difference.
 *
 * ## The one environment where this throws
 *
 * `crypto.subtle` requires a secure context. The site is HTTPS-only; on a
 * plain-HTTP dev server this rejects. Callers decide what that means for them —
 * `/prepare` swallows it (a dev page should not enqueue production cards),
 * while the report dialog surfaces a retry, because a person who wrote out a
 * bug report is owed an answer either way.
 */

/** Hashes the exact bytes that will be sent, never a re-encoding of them. */
export async function bodyHash(body: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(body));
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}
